import { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import language from '../../variables/language';
import IconDone from '../../components/icons/IconDone';
import IconWrong from '../../components/icons/IconWrong';
import IconKeys from '../../components/icons/IconKeys';
import IconCopyUrl from '../../components/icons/IconCopyUrl';
import IconAdd from '../../components/icons/IconAdd';
import IconEdit from '../../components/icons/IconEdit';
import IconDelete from '../../components/icons/IconDelete';
import { getNotificationType } from '../../variables/notificationTypes';
import * as commApi from '../communications/api';
import AssignNewCommunicationModal from '../communications/AssignNewCommunicationModal';
import * as rescheduleApi from './rescheduleApi';
import RescheduleResponseModal from './RescheduleResponseModal';
import useDayParts from '../../hooks/useDayParts';
import wsManager from '../../services/wsManager';
import '../../css/platform-access-modal.css';
import '../../css/communications.css';
import '../../css/reschedule.css';
import '../../css/student-platform-tab.css';

// "Платформа" tab - only rendered once a student has a UserPlatform account
// (see StudentCard.jsx). Layout: one account strip on top (status switch +
// password reset), then two equal tiles side by side - "Інформація" (what the
// teacher sent the student) on the left, "Очікують підтвердження" (what the
// student still has to answer or the teacher has to decide) on the right.
// Self-fetches `platformUser` independently rather than receiving it as a prop
// from StudentCard.jsx (which already has one) - matches this module's
// established "self-fetching via useParams" convention.
export default function StudentPlatform() {
   const { id } = useParams();
   const navigate = useNavigate();
   const lang = language[language.lang].platformTab;
   const topLang = language[language.lang];

   const [requests, setRequests] = useState([]);
   const [status, setStatus] = useState('loading');
   const [platformUser, setPlatformUser] = useState(null);
   const [resetLink, setResetLink] = useState('');
   const [resetting, setResetting] = useState(false);
   const [resetError, setResetError] = useState('');
   const [copied, setCopied] = useState(false);
   const [togglingActive, setTogglingActive] = useState(false);
   const [activeError, setActiveError] = useState('');

   const [notifications, setNotifications] = useState([]);
   const [templates, setTemplates] = useState([]);
   const [showPicker, setShowPicker] = useState(false);
   const [pickerValue, setPickerValue] = useState('');
   const [showCreateModal, setShowCreateModal] = useState(false);

   const [negotiationItems, setNegotiationItems] = useState([]);
   const [rescheduleModalItem, setRescheduleModalItem] = useState(null);
   const [upcomingLessonIds, setUpcomingLessonIds] = useState([]);
   const dayParts = useDayParts();

   const loadNegotiations = () => {
      Promise.all([
         rescheduleApi.getPendingRescheduleRequests(id).catch(() => ({ data: [] })),
         rescheduleApi.getPendingCancelRequests(id).catch(() => ({ data: [] })),
      ]).then(([rescheduleRes, cancelRes]) => {
         const reschedules = (Array.isArray(rescheduleRes.data) ? rescheduleRes.data : []).map((r) => ({ type: 'reschedule', id: r.id, raw: r }));
         const cancels = (Array.isArray(cancelRes.data) ? cancelRes.data : []).map((r) => ({ type: 'cancel', id: r.id, raw: r }));
         setNegotiationItems([...reschedules, ...cancels]);
      });
   };

   const loadNotifications = () => {
      commApi.getStudentNotifications(id).then(res => {
         setNotifications(Array.isArray(res.data) ? res.data : []);
      }).catch(() => {});
   };

   const load = () => {
      axios.get('/api/teacher-approval-requests', { params: { student: id }, withCredentials: true })
         .then(response => {
            setRequests(Array.isArray(response.data) ? response.data : []);
            setStatus('done');
         })
         .catch(err => {
            if (err.response && err.response.status === 401) navigate('/login');
            setStatus('done');
         });
   }

   useEffect(() => {
      load();
      loadNotifications();
      loadNegotiations();
      // Same "subscribe to every upcoming lesson's topic" approach as
      // StudentCard.jsx's header badge, so this list also updates live
      // instead of only refreshing after the teacher's own in-app actions.
      axios.get('/api/events?student=' + id + '&dateFrom=' + new Date().toISOString().slice(0, 10), { withCredentials: true })
         .then(response => {
            const events = Array.isArray(response.data) ? response.data : [];
            setUpcomingLessonIds([...new Set(events.map(ev => ev.lesson && ev.lesson.id).filter(Boolean))]);
         })
         .catch(() => setUpcomingLessonIds([]));
      axios.get('/api/platform-users', { params: { studentId: id }, withCredentials: true })
         .then(response => {
            const data = response.data;
            const user = Array.isArray(data) ? data[0] : data;
            setPlatformUser(user || null);
         })
         .catch(() => setPlatformUser(null));
      setResetLink('');
      setResetError('');
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id]);

   useEffect(() => {
      if(!upcomingLessonIds.length) return;
      const handler = () => loadNegotiations();
      upcomingLessonIds.forEach(lessonId => wsManager.subscribe('/topic/reschedule/' + lessonId, handler));
      return () => upcomingLessonIds.forEach(lessonId => wsManager.unsubscribe('/topic/reschedule/' + lessonId, handler));
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [upcomingLessonIds]);

   const openPicker = () => {
      commApi.getNotifications().then(res => setTemplates(Array.isArray(res.data) ? res.data : [])).catch(() => {});
      setPickerValue('');
      setShowPicker(true);
   };
   const handleAssignExisting = () => {
      if (!pickerValue) return;
      commApi.assignNotification(id, pickerValue).then(() => {
         setShowPicker(false);
         loadNotifications();
      }).catch(err => console.log(err));
   };
   const handleUnassign = (studentNotificationId) => {
      commApi.unassignNotification(studentNotificationId).then(loadNotifications).catch(err => console.log(err));
   };
   const handleAssignedNew = () => {
      setShowCreateModal(false);
      loadNotifications();
   };

   // Resolved requests never come back from this endpoint (server-side
   // filters to PENDING only) - approving/rejecting here just means "reload
   // and it's naturally gone", no client-side removal bookkeeping needed.
   const handleApprove = (requestId) => {
      axios.post('/api/teacher-approval-requests/' + requestId + '/approve', {}, { withCredentials: true })
         .then(load).catch(err => console.log(err));
   }
   const handleReject = (requestId) => {
      axios.post('/api/teacher-approval-requests/' + requestId + '/reject', {}, { withCredentials: true })
         .then(load).catch(err => console.log(err));
   }

   // Same "raw link back in the API response, teacher copies and hands it
   // to the student themselves" shape as account creation
   // (PlatformAccessModal.jsx) - nothing in this app is ever emailed.
   const handleResetPassword = () => {
      if (!platformUser || resetting) return;
      setResetting(true);
      setResetError('');
      axios.post('/api/platform-users/' + platformUser.id + '/reset-password', {}, { withCredentials: true })
         .then(response => {
            setResetLink(response.data.resetPasswordLink || '');
            setCopied(false);
         })
         .catch(() => setResetError(lang.resetPasswordError))
         .finally(() => setResetting(false));
   }
   const handleCopy = () => {
      if (!resetLink) return;
      navigator.clipboard.writeText(resetLink).then(() => {
         setCopied(true);
         setTimeout(() => setCopied(false), 1500);
      }).catch(() => {});
   }

   // Reuses UserPlatform.active as-is (already the exact field checkLogin()
   // rejects on server-side, per PlatformUserController's own PUT contract) -
   // this is purely a new UI on top of an existing, already-enforced flag,
   // not a new capability on the backend. Optimistic local update, reverted
   // on failure so the switch never silently drifts from server state.
   const handleToggleActive = () => {
      if (!platformUser || togglingActive) return;
      const nextActive = !platformUser.active;
      setTogglingActive(true);
      setActiveError('');
      axios.put('/api/platform-users/' + platformUser.id, JSON.stringify({ active: String(nextActive) }), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      })
         .then(() => setPlatformUser((prev) => ({ ...prev, active: nextActive })))
         .catch(() => setActiveError(lang.accountActiveError))
         .finally(() => setTogglingActive(false));
   }

   const isActive = !!(platformUser && platformUser.active);
   const pendingCount = requests.length + negotiationItems.length;

   return (
      <div className='sp-root'>
         <section className='sp-account'>
            <div className='sp-account-row'>
               <label className={'sp-switch' + (isActive ? ' is-on' : '') + (togglingActive ? ' is-pending' : '')}
                  title={lang.accountActiveLabel}>
                  <input type='checkbox' checked={isActive} disabled={!platformUser || togglingActive} onChange={handleToggleActive} />
                  <span className='sp-switch-track'><span className='sp-switch-thumb'></span></span>
                  <span className='sp-switch-label'>{isActive ? lang.accountStatusOn : lang.accountStatusOff}</span>
               </label>
               <button type='button' className='sp-btn sp-btn-ghost' disabled={!platformUser || resetting || !isActive} onClick={handleResetPassword}>
                  <IconKeys />
                  <span>{resetting ? topLang['Loading ...'] : lang.resetPasswordButton}</span>
               </button>
               {activeError ? <p className='sp-error'>{activeError}</p> : ''}
               {resetError ? <p className='sp-error'>{resetError}</p> : ''}
            </div>
            {resetLink ? (
               <div className='sp-link-box'>
                  <span className='sp-link-text'>{resetLink}</span>
                  <button type='button' className='sp-btn sp-btn-ghost sp-btn-sm' onClick={handleCopy} title={topLang.copyActivationLink}>
                     <IconCopyUrl />
                     <span>{copied ? topLang.studentsPage.copied : topLang.copyActivationLink}</span>
                  </button>
                  <p className='sp-hint'>{lang.resetPasswordHint}</p>
               </div>
            ) : ''}
         </section>

         <div className='sp-split'>
            <section className='sp-tile'>
               <header className='sp-tile-head'>
                  <div className='sp-tile-title'>
                     <span>Інформація</span>
                     <span className='sp-count'>{notifications.length}</span>
                  </div>
                  <div className='sp-tile-actions'>
                     <button type='button' className='sp-btn sp-btn-ghost sp-btn-sm' onClick={openPicker}>
                        Додати існуючу
                     </button>
                     <button type='button' className='sp-btn sp-btn-primary sp-btn-sm' onClick={() => setShowCreateModal(true)}>
                        <IconAdd />
                        <span>Створити нову</span>
                     </button>
                  </div>
               </header>
               {showPicker ? (
                  <div className='sp-picker'>
                     <select className='sp-select' value={pickerValue} onChange={(e) => setPickerValue(e.target.value)}>
                        <option value=''>Оберіть інформацію...</option>
                        {templates.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                     </select>
                     <button type='button' className='sp-btn sp-btn-primary sp-btn-sm' disabled={!pickerValue} onClick={handleAssignExisting}>Додати</button>
                     <button type='button' className='sp-btn sp-btn-ghost sp-btn-sm' onClick={() => setShowPicker(false)}>Скасувати</button>
                  </div>
               ) : ''}
               <div className='sp-tile-body'>
                  {notifications.length === 0 ? (
                     <div className='sp-empty'>У студента ще немає жодної інформації</div>
                  ) : ''}
                  {notifications.map(sn => {
                     const notif = sn.notification || {};
                     const t = getNotificationType(notif.type);
                     const needsAcceptance = notif.type === 'ACK_REQUIRED' || notif.type === 'BLOCKING';
                     return (
                        <article className='sp-card' key={sn.id} style={t ? { '--accent': t.accent } : undefined}>
                           <div className='sp-card-main'>
                              <div className='sp-card-title'>{notif.title}</div>
                              <div className='sp-card-meta'>
                                 {t ? <span className='sp-badge' style={{ color: t.accent, borderColor: t.accent }}>{t.label}</span> : ''}
                                 {needsAcceptance ? (
                                    <span className={'sp-chip ' + (sn.accepted ? 'is-ok' : 'is-wait')}>
                                       {sn.accepted ? 'Підтверджено' : 'Не підтверджено'}
                                    </span>
                                 ) : ''}
                              </div>
                              <div className='sp-card-dates'>
                                 Додано {sn.dAdd ? sn.dAdd.replace('T', ' ').slice(0, 16) : '—'}
                                 {sn.accepted ? ' · підтверджено ' + (sn.dAccepted ? sn.dAccepted.replace('T', ' ').slice(0, 16) : '—') : ''}
                              </div>
                           </div>
                           <div className='sp-card-actions'>
                              <button type='button' className='sp-icon-btn' onClick={() => navigate('/communications/' + notif.id)} title='Редагувати'>
                                 <IconEdit />
                              </button>
                              <button type='button' className='sp-icon-btn is-danger' onClick={() => handleUnassign(sn.id)} title='Прибрати у студента'>
                                 <IconDelete />
                              </button>
                           </div>
                        </article>
                     );
                  })}
               </div>
            </section>

            <section className='sp-tile'>
               <header className='sp-tile-head'>
                  <div className='sp-tile-title'>
                     <span>{lang.pendingRequestsTitle}</span>
                     <span className={'sp-count' + (pendingCount > 0 ? ' is-alert' : '')}>{pendingCount}</span>
                  </div>
               </header>
               <div className='sp-tile-body'>
                  {status === 'done' && pendingCount === 0 ? (
                     <div className='sp-empty'>{lang.noPendingRequests}</div>
                  ) : ''}
                  {requests.map(req => (
                     <article className='sp-card' key={'req-' + req.id}>
                        <div className='sp-card-main'>
                           <div className='sp-card-title'>{req.message}</div>
                        </div>
                        <div className='sp-card-actions sp-card-actions-wide'>
                           <button type='button' className='sp-btn sp-btn-ok sp-btn-sm' onClick={() => handleApprove(req.id)} title={lang.approve}>
                              <IconDone />
                              <span>{lang.approve}</span>
                           </button>
                           <button type='button' className='sp-btn sp-btn-danger sp-btn-sm' onClick={() => handleReject(req.id)} title={lang.reject}>
                              <IconWrong />
                              <span>{lang.reject}</span>
                           </button>
                        </div>
                     </article>
                  ))}
                  {negotiationItems.map((item) => {
                     const isReschedule = item.type === 'reschedule';
                     const raw = item.raw;
                     const lesson = isReschedule ? (raw.lesson || {}) : ((raw.event || {}).lesson || {});
                     const studentName = isReschedule ? (raw.initiatedBy ? (raw.initiatedBy.name + ' ' + raw.initiatedBy.surname) : '') : (raw.student ? (raw.student.name + ' ' + raw.student.surname) : '');
                     const dayPart = isReschedule ? dayParts.find((dp) => dp.id === raw.currentProposedDayPart) : null;
                     const summary = isReschedule
                        ? studentName + ' пропонує перенести заняття ' + (lesson.date || '') + (raw.currentProposedDate ? ' на ' + raw.currentProposedDate : '') + (dayPart ? ' (' + dayPart.label + ')' : '')
                        : studentName + ' просить скасувати заняття ' + (lesson.date || '') + (lesson.start ? ', ' + lesson.start : '');
                     return (
                        <article className={'sp-card sp-card-' + item.type} key={item.type + '-' + item.id}>
                           <div className='sp-card-main'>
                              <div className='sp-card-meta'>
                                 <span className={'sp-badge sp-badge-' + item.type}>{isReschedule ? 'Зміна терміну' : 'Скасування'}</span>
                              </div>
                              <div className='sp-card-title sp-card-title-regular'>{summary}</div>
                           </div>
                           <div className='sp-card-actions sp-card-actions-wide'>
                              <button type='button' className='sp-btn sp-btn-ghost sp-btn-sm' onClick={() => setRescheduleModalItem(item)}>
                                 Розглянути
                              </button>
                           </div>
                        </article>
                     );
                  })}
               </div>
            </section>
         </div>

         <AssignNewCommunicationModal isOpen={showCreateModal} studentId={id} onClose={() => setShowCreateModal(false)} onAssigned={handleAssignedNew} />
         <RescheduleResponseModal key={rescheduleModalItem ? rescheduleModalItem.type + '-' + rescheduleModalItem.id : 'none'}
            isOpen={!!rescheduleModalItem} item={rescheduleModalItem}
            onClose={() => setRescheduleModalItem(null)}
            onResolved={loadNegotiations} />
      </div>
   );
}
